import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trash2, Loader2, Eye, ChevronDown, Phone } from "lucide-react";
import { StatusBadge } from "./StatusBadge";
import {
  updateOrderCity,
  updateClientCity,
  scoring,
} from "../../../../../api/auth";
import { Select } from "../ui/Select";
import {
  PRIMARY_STATUSES,
  OTHER_STATUSES,
  TONE_CLASSES,
} from "../ui/orderStatuses";

/* ===============================
   STATUS CONFIG
   Valeurs = integration_service Order.LOCAL_STATUS (voir ui/orderStatuses.js).
   Les anciennes valeurs (not_interested, unreachable, no_answer2...) étaient
   refusées par le backend ("Unsupported status value.").
================================ */

/* ===============================
   AGENCY CAPABILITIES
================================ */

const AGENCY_CAPABILITIES = {
  "9ada9315-c969-4092-9e2f-84d7f171ebdf": {
    name: "onessta",
    is_exchange: true,
    is_fragile: false,
    is_open: true,
  },
  "e48eeff4-001c-4d04-aefa-4fc4db10bac4": {
    name: "forclog",
    is_exchange: false,
    is_fragile: false,
    is_open: true,
  },
  "8136488d-fb1b-4696-9a16-a8cf968d9b38": {
    name: "ozon",
    is_exchange: true,
    is_fragile: true,
    is_open: true,
  },
  "cdd55a22-38ee-11f1-867f-0200c32b60b0": {
    name: "olivraison",
    is_exchange: false, // l'échange Olivraison exige le trackingID du colis d'origine
    is_fragile: false,
    is_open: true, // noOpen
  },
  "62dcb8b0-0470-4dbf-bcca-13aa9a618f71": {
    name: "ameex",
    is_exchange: true,
    is_fragile: true,
    is_open: true,
  },
};

const DEFAULT_CAPABILITIES = {
  is_exchange: false,
  is_fragile: false,
  is_open: false,
};

/* ===============================
   HELPERS
================================ */

const validatePhone = (phone) => /^0\d{9}$/.test((phone || "").trim());

const normalizePhoneForScoring = (phone) => {
  if (!phone) return "";
  let cleaned = String(phone).replace(/\D/g, "");

  if (cleaned.startsWith("0")) cleaned = cleaned.slice(1);
  if (cleaned.startsWith("212")) cleaned = cleaned.slice(3);

  return cleaned;
};

const getScoreVisual = (scoreData) => {
  if (!scoreData || scoreData.isNewClient) {
    return {
      label: "Nouveau client",
      className: "bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-300 dark:border-slate-700",
    };
  }

  if (scoreData.category === "BLACKLIST") {
    return {
      label: `Blacklist - ${Math.round(Number(scoreData.score ?? 0))}/100`,
      className: "bg-black text-white border-black",
    };
  }

  const score = Number(scoreData.score ?? 0);

  if (score <= 24) {
    return {
      label: `Black - ${Math.round(score)}/100`,
      className: "bg-black text-white border-black",
    };
  }

  if (score <= 49) {
    return {
      label: `Red - ${Math.round(score)}/100`,
      className: "bg-red-100 dark:bg-rose-500/10 text-red-700 dark:text-rose-400 border-red-300 dark:border-rose-500/30",
    };
  }

  if (score <= 74) {
    return {
      label: `Blue - ${Math.round(score)}/100`,
      className: "bg-blue-100 dark:bg-indigo-500/10 text-blue-700 dark:text-indigo-400 border-blue-300 dark:border-indigo-500/30",
    };
  }

  return {
    label: `Green - ${Math.round(score)}/100`,
    className: "bg-green-100 dark:bg-emerald-500/10 text-green-700 dark:text-emerald-400 border-green-300 dark:border-emerald-500/30",
  };
};

/* ===============================
   MAIN COMPONENT
================================ */

export function OrdersTable({
  orders,
  drafts,
  onToggleSelect,
  selectedIds,
  onView,
  onEditNote,
  onQuickStatus,
  saving,
  mode = "all",
  onRefresh,
  city = [],
  onOrderPatched = () => {},
  dispatchValidation = false,
  dispatchAttempted = false,
  agencyId = "",
}) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const [orderFront, setOrderFront] = useState(orders || []);
  const [clientScores, setClientScores] = useState({});
  const [loadingScores, setLoadingScores] = useState({});
  const [cityErrors, setCityErrors] = useState({});
  const [phoneErrors, setPhoneErrors] = useState({});
  const [rowLoading, setRowLoading] = useState({});

  useEffect(() => {
    setOrderFront(orders || []);
  }, [orders]);

  const agencyCapabilities = useMemo(() => {
    return AGENCY_CAPABILITIES[agencyId] || DEFAULT_CAPABILITIES;
  }, [agencyId]);

  const showExchange = mode !== "new" && agencyCapabilities.is_exchange;
  const showFragile = mode !== "new" && agencyCapabilities.is_fragile;
  const showOpen = mode !== "new" && agencyCapabilities.is_open;

  const setRowActionLoading = useCallback((orderId, action, value) => {
    setRowLoading((prev) => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        [action]: value,
      },
    }));
  }, []);

  const isRowActionLoading = useCallback(
    (orderId, action) => !!rowLoading?.[orderId]?.[action],
    [rowLoading]
  );

  const isRowBusy = useCallback(
    (orderId) => {
      const actions = rowLoading?.[orderId];
      if (!actions) return false;
      return Object.values(actions).some(Boolean);
    },
    [rowLoading]
  );

  const validateOrderForDispatch = useCallback((order) => {
    const phoneValid = validatePhone(order?.client_phone || "");
    const cityValid = !!order?.shipping_city;

    return {
      phoneValid,
      cityValid,
      isValid: phoneValid && cityValid,
    };
  }, []);

  useEffect(() => {
    if (!dispatchValidation || !dispatchAttempted) return;

    const nextPhoneErrors = {};
    const nextCityErrors = {};

    orderFront.forEach((order) => {
      if (!selectedSet.has(order.id)) return;

      const check = validateOrderForDispatch(order);
      nextPhoneErrors[order.id] = !check.phoneValid;
      nextCityErrors[order.id] = !check.cityValid;
    });

    setPhoneErrors((prev) => ({ ...prev, ...nextPhoneErrors }));
    setCityErrors((prev) => ({ ...prev, ...nextCityErrors }));
  }, [
    dispatchValidation,
    dispatchAttempted,
    orderFront,
    selectedSet,
    validateOrderForDispatch,
  ]);

  const fetchClientScore = useCallback(async (order) => {
    try {
      const normalizedPhone = normalizePhoneForScoring(order.client_phone);

      if (!normalizedPhone) {
        setClientScores((prev) => ({
          ...prev,
          [order.id]: {
            score: null,
            category: null,
            raw: null,
            isNewClient: true,
          },
        }));
        return;
      }

      setLoadingScores((prev) => ({
        ...prev,
        [order.id]: true,
      }));

      const res = await scoring(normalizedPhone);
      const data = res?.data;

      setClientScores((prev) => ({
        ...prev,
        [order.id]: {
          score: data?.final?.score_100 ?? null,
          category: data?.final?.category?.value ?? null,
          raw: data ?? null,
          isNewClient: (data?.final?.orders_lifetime ?? 0) === 0,
        },
      }));
    } catch (err) {
      console.error("Scoring fetch error", err);

      setClientScores((prev) => ({
        ...prev,
        [order.id]: {
          score: null,
          category: null,
          raw: null,
          isNewClient: true,
          error: true,
        },
      }));
    } finally {
      setLoadingScores((prev) => ({
        ...prev,
        [order.id]: false,
      }));
    }
  }, []);

  useEffect(() => {
    if (!orderFront?.length) return;

    const missingOrders = orderFront.filter(
      (order) =>
        order?.id &&
        clientScores[order.id] === undefined &&
        loadingScores[order.id] !== true
    );

    if (!missingOrders.length) return;

    missingOrders.forEach((order) => {
      fetchClientScore(order);
    });
  }, [orderFront, clientScores, loadingScores, fetchClientScore]);

  const handleCityChange = useCallback(
    async (orderId, cityName, idclient) => {
      setRowActionLoading(orderId, "city", true);

      try {
        await Promise.all([
          updateOrderCity(orderId, { shipping_city: cityName }),
          updateClientCity(idclient, { city: cityName }),
        ]);

        setOrderFront((prev) =>
          prev.map((order) =>
            order.id === orderId ? { ...order, shipping_city: cityName } : order
          )
        );

        onOrderPatched(orderId, { shipping_city: cityName });

        setCityErrors((prev) => ({
          ...prev,
          [orderId]: !cityName,
        }));
      } catch (err) {
        console.error("City update error", err);
      } finally {
        setRowActionLoading(orderId, "city", false);
      }
    },
    [setRowActionLoading, onOrderPatched]
  );

  const handlePhoneChange = useCallback(
    async (orderId, phone, idclient) => {
      const cleanedPhone = (phone || "").trim();

      if (!validatePhone(cleanedPhone)) {
        setPhoneErrors((prev) => ({ ...prev, [orderId]: true }));
        return;
      }

      setPhoneErrors((prev) => ({ ...prev, [orderId]: false }));
      setRowActionLoading(orderId, "phone", true);

      try {
        await Promise.all([
          updateOrderCity(orderId, { client_phone: cleanedPhone }),
          updateClientCity(idclient, { phone: cleanedPhone }),
        ]);

        setOrderFront((prev) =>
          prev.map((order) =>
            order.id === orderId
              ? { ...order, client_phone: cleanedPhone }
              : order
          )
        );

        onOrderPatched(orderId, { client_phone: cleanedPhone });
      } catch (err) {
        console.error("Phone update error", err);
      } finally {
        setRowActionLoading(orderId, "phone", false);
      }
    },
    [setRowActionLoading, onOrderPatched]
  );

  const handleMarkShippingNon = useCallback(
    async (orderId) => {
      setRowActionLoading(orderId, "delete", true);

      try {
        await updateOrderCity(orderId, { shipping_status: "non" });

        setOrderFront((prev) => prev.filter((order) => order.id !== orderId));

        if (onRefresh) {
          await onRefresh();
        }
      } catch (err) {
        console.error("Shipping status update error", err);
      } finally {
        setRowActionLoading(orderId, "delete", false);
      }
    },
    [onRefresh, setRowActionLoading]
  );

  const handleQuickStatusLocal = useCallback(
    async (orderId, status) => {
      const order = orderFront.find((o) => o.id === orderId);
      if (!order) return;

      if (status === "confirmed") {
        const check = validateOrderForDispatch(order);

        setPhoneErrors((prev) => ({
          ...prev,
          [orderId]: !check.phoneValid,
        }));

        setCityErrors((prev) => ({
          ...prev,
          [orderId]: !check.cityValid,
        }));

        if (!check.isValid) return;
      }

      setRowActionLoading(orderId, "status", true);

      try {
        await onQuickStatus(orderId, status);
      } catch (err) {
        console.error("Quick status update error", err);
      } finally {
        setRowActionLoading(orderId, "status", false);
      }
    },
    [orderFront, onQuickStatus, validateOrderForDispatch, setRowActionLoading]
  );

  const handleDeliveryOptionChange = useCallback(
    async (orderId, field, checked) => {
      setRowActionLoading(orderId, field, true);

      try {
        const payload = {
          [field]: checked ? 1 : 0,
        };

        await updateOrderCity(orderId, payload);

        setOrderFront((prev) =>
          prev.map((order) =>
            order.id === orderId
              ? { ...order, [field]: checked ? 1 : 0 }
              : order
          )
        );

        onOrderPatched(orderId, { [field]: checked ? 1 : 0 });
      } catch (err) {
        console.error(`${field} update error`, err);
      } finally {
        setRowActionLoading(orderId, field, false);
      }
    },
    [setRowActionLoading, onOrderPatched]
  );

  if (!orderFront.length) {
    return (
      <div className="empty-state m-4 flex flex-col items-center gap-1 rounded-lg bg-slate-50 p-10 text-center dark:bg-slate-800">
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Aucune commande</p>
        <p className="text-xs text-slate-500">Rien à traiter avec ces filtres.</p>
      </div>
    );
  }

  return (
    <div className="data-table-shell overflow-hidden">
      <div className="overflow-auto">
        <table className="w-full min-w-[980px] text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800">
            <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
              <Th className="w-10" />
              <Th>Client</Th>
              <Th>Commande</Th>
              <Th>Ville</Th>
              <Th>Statut</Th>
              <Th className="text-right">Montant</Th>
              <Th>Score</Th>
              {mode === "new" && <Th>Traitement</Th>}
              {showExchange && <Th>Échange</Th>}
              {showFragile && <Th>Fragile</Th>}
              {showOpen && <Th>Ouvrable</Th>}
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>

          <tbody>
            {orderFront.map((o) => (
              <OrderRow
                key={o.id}
                o={o}
                draft={drafts?.[o.id] || {}}
                isSelected={selectedSet.has(o.id)}
                mode={mode}
                saving={saving}
                city={city}
                cityError={!!cityErrors[o.id]}
                phoneError={!!phoneErrors[o.id]}
                scoreData={clientScores[o.id]}
                scoreLoading={!!loadingScores[o.id]}
                rowBusy={isRowBusy(o.id)}
                cityLoading={isRowActionLoading(o.id, "city")}
                phoneLoading={isRowActionLoading(o.id, "phone")}
                deleteLoading={isRowActionLoading(o.id, "delete")}
                statusLoading={isRowActionLoading(o.id, "status")}
                exchangeLoading={isRowActionLoading(o.id, "is_exchange")}
                fragileLoading={isRowActionLoading(o.id, "is_fragile")}
                openLoading={isRowActionLoading(o.id, "is_open")}
                onToggleSelect={onToggleSelect}
                onView={onView}
                onPhoneChange={handlePhoneChange}
                onCityChange={handleCityChange}
                onQuickStatus={handleQuickStatusLocal}
                onDeliveryOptionChange={handleDeliveryOptionChange}
                onDelete={handleMarkShippingNon}
                showExchange={showExchange}
                showFragile={showFragile}
                showOpen={showOpen}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`whitespace-nowrap px-3 py-3 ${className}`}>{children}</th>;
}

function Td({ children, className = "" }) {
  return <td className={`px-3 py-3 align-middle text-slate-700 dark:text-slate-300 ${className}`}>{children}</td>;
}

const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || "")
    .join("")
    .toUpperCase() || "?";

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

/* ===============================
   ORDER ROW
================================ */

const OrderRow = React.memo(function OrderRow({
  o,
  draft,
  isSelected,
  mode,
  saving,
  city,
  cityError,
  phoneError,
  scoreData,
  scoreLoading,
  rowBusy,
  cityLoading,
  phoneLoading,
  deleteLoading,
  statusLoading,
  exchangeLoading,
  fragileLoading,
  openLoading,
  onToggleSelect,
  onView,
  onPhoneChange,
  onCityChange,
  onQuickStatus,
  onDeliveryOptionChange,
  onDelete,
  showExchange,
  showFragile,
  showOpen,
}) {
  const localStatus = draft.local_status ?? o.local_status;
  const visual = getScoreVisual(scoreData);
  const disabled = saving || rowBusy;

  return (
    <tr
      className={`group border-t border-slate-100 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800 ${
        isSelected ? "bg-indigo-50" : ""
      } ${rowBusy ? "opacity-80" : ""}`}
    >
      <Td className="w-10">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onToggleSelect(o.id, e.target.checked)}
          className="h-4 w-4 accent-indigo-600"
          disabled={disabled}
          aria-label="Sélectionner la commande"
        />
      </Td>

      {/* Client */}
      <Td className="min-w-[220px]">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-bold text-indigo-600">
            {initials(o.client_name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold text-slate-900 dark:text-slate-100">{o.client_name || "—"}</div>
            <div className="relative mt-1">
              <Phone className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                defaultValue={o.client_phone}
                onBlur={(e) => {
                  if (e.target.value !== o.client_phone) onPhoneChange(o.id, e.target.value, o.client);
                }}
                disabled={saving || phoneLoading}
                className={`w-full min-w-[150px] rounded-md border py-1 pl-7 pr-7 text-xs text-slate-900 dark:text-slate-100 ${
                  phoneError ? "border-rose-400 bg-rose-50" : "border-slate-200 dark:border-slate-700"
                } ${phoneLoading ? "opacity-70" : ""}`}
              />
              {phoneLoading && (
                <Loader2 className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-slate-400" />
              )}
            </div>
            {phoneError && (
              <div className="mt-1 text-[10px] text-rose-600">Numéro invalide (10 chiffres, commence par 0)</div>
            )}
          </div>
        </div>
      </Td>

      {/* Commande */}
      <Td className="min-w-[200px]">
        <div className="line-clamp-2 font-medium text-slate-900 dark:text-slate-100" title={o.produit}>
          {o.produit || "—"}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
          {o.variant ? <span className="font-medium text-indigo-600">{o.variant}</span> : null}
          <span>{formatDate(o.created_at)}</span>
          {o.is_duplicate && (
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${TONE_CLASSES.danger}`}>Doublon</span>
          )}
          {o.is_priority && (
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${TONE_CLASSES.warning}`}>Prioritaire</span>
          )}
        </div>
      </Td>

      {/* Ville */}
      <Td className="min-w-[170px]">
        <div className="relative">
          <Select
            value={o.shipping_city}
            options={city}
            onChange={(cityValue) => onCityChange(o.id, cityValue, o.client)}
            placeholder="Choisir la ville"
            disabled={saving || cityLoading}
          />
          {cityLoading && (
            <Loader2 className="pointer-events-none absolute right-8 top-2.5 h-4 w-4 animate-spin text-slate-400" />
          )}
        </div>
        {cityError && <div className="mt-1 text-[10px] text-rose-600">Ville obligatoire</div>}
      </Td>

      <Td>
        <StatusBadge local_status={localStatus} shipping_status={o.shipping_status} />
      </Td>

      <Td className="whitespace-nowrap text-right font-semibold text-slate-900 dark:text-slate-100">
        {o.total_amount} <span className="text-xs font-medium text-slate-500">{o.currency}</span>
      </Td>

      <Td>
        {scoreLoading ? (
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Score…
          </span>
        ) : (
          <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${visual.className}`}>
            {visual.label}
          </span>
        )}
      </Td>

      {mode === "new" && (
        <Td className="min-w-[260px]">
          <WorkflowActions
            orderId={o.id}
            currentStatus={localStatus}
            onQuickStatus={onQuickStatus}
            saving={saving || statusLoading}
            loading={statusLoading}
          />
        </Td>
      )}

      {showExchange && (
        <Td>
          <OptionToggle
            checked={!!o.is_exchange}
            loading={exchangeLoading}
            disabled={saving || exchangeLoading}
            onChange={(checked) => onDeliveryOptionChange(o.id, "is_exchange", checked)}
          />
        </Td>
      )}

      {showFragile && (
        <Td>
          <OptionToggle
            checked={!!o.is_fragile}
            loading={fragileLoading}
            disabled={saving || fragileLoading}
            onChange={(checked) => onDeliveryOptionChange(o.id, "is_fragile", checked)}
          />
        </Td>
      )}

      {showOpen && (
        <Td>
          <OptionToggle
            checked={!!o.is_open}
            loading={openLoading}
            disabled={saving || openLoading}
            onChange={(checked) => onDeliveryOptionChange(o.id, "is_open", checked)}
          />
        </Td>
      )}

      <Td className="text-right">
        <div className="inline-flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onView(o)}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200"
            title="Détails de la commande et historique du client"
          >
            <Eye className="h-3.5 w-3.5" /> Détails
          </button>
          <button
            type="button"
            onClick={() => onDelete(o.id)}
            className="inline-flex items-center justify-center rounded-lg border border-rose-200 bg-rose-50 p-1.5 text-rose-600 transition hover:bg-rose-100 disabled:opacity-50"
            disabled={saving || deleteLoading}
            title="Retirer de la liste"
          >
            {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        </div>
      </Td>
    </tr>
  );
});

function OptionToggle({ checked, loading, disabled, onChange }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-indigo-600"
      />
      {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
    </label>
  );
}

/* ===============================
   WORKFLOW ACTIONS COMPONENT
================================ */

function WorkflowActions({ orderId, currentStatus, onQuickStatus, saving, loading }) {
  const [open, setOpen] = useState(false);

  const pick = (value) => {
    setOpen(false);
    onQuickStatus(orderId, value);
  };

  return (
    <div className="relative flex flex-wrap items-center gap-1.5">
      {PRIMARY_STATUSES.map((s) => {
        const active = currentStatus === s.value;
        return (
          <button
            key={s.value}
            type="button"
            disabled={saving}
            onClick={() => pick(s.value)}
            className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${
              s.value === "confirmed"
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : `${TONE_CLASSES[s.tone]} hover:opacity-80`
            } ${active ? "ring-2 ring-indigo-400 ring-offset-1" : ""}`}
          >
            {loading && s.value === "confirmed" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : s.label}
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={saving}
        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300"
        aria-expanded={open}
      >
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Autre"}
        <ChevronDown className={`h-3.5 w-3.5 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && !loading && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-20 cursor-default"
            aria-label="Fermer"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-9 z-30 max-h-72 w-60 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
            {OTHER_STATUSES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => pick(s.value)}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <span className={`h-2 w-2 rounded-full ${TONE_CLASSES[s.tone]}`} />
                {s.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
